import * as React from 'react';
import { Box, Button, Chip, IconButton, Stack, SvgIcon, Typography, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { Paperclip as PaperclipIcon, Download as DownloadIcon, Eye as EyeIcon } from '@phosphor-icons/react/dist/ssr';
import ProfessionalDialog, { DialogActionButtons } from '@/components/common/professional-dialog';
import { getS3PresignedUrl } from '@/utils/s3';
import type { File as V2File } from '@/types/v2/file';
import { config } from '@/config';

export interface AnnouncementFileItem {
  id?: string | number;
  key: string;
  path: string;
  size: number;
  name: string;
  type: string;
}

interface AnnouncementAttachmentsDialogProps {
  open: boolean;
  onClose: () => void;
  files: AnnouncementFileItem[];
}

export default function AnnouncementAttachmentsDialog({ open, onClose, files }: AnnouncementAttachmentsDialogProps) {
  const [urlMap, setUrlMap] = useState<Record<string, string>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const fetchUrls = async () => {
      if (!files || files.length === 0) return;
      const entries = await Promise.all(
        files.map(async (f) => {
          const v2File: V2File = {
            id: String(f.id ?? f.key),
            name: f.name,
            path: f.path,
            mime_type: f.type,
            size: f.size,
          } as any;
          try {
            const url = await getS3PresignedUrl(v2File);
            return [String(f.id ?? f.key), url] as const;
          } catch {
            return [String(f.id ?? f.key), `${config.site.api}/api/v2/files/${f.id ?? f.key}`] as const;
          }
        })
      );
      setUrlMap(Object.fromEntries(entries));
      // Set default selected file (first)
      setSelectedId(String(files[0]?.id ?? files[0]?.key ?? ''));
    };
    fetchUrls();
  }, [files]);

  const selectedFile = React.useMemo(() => {
    if (!files || !selectedId) return null;
    return files.find((f) => String(f.id ?? f.key) === selectedId) || null;
  }, [files, selectedId]);

  const selectedUrl = selectedId ? urlMap[selectedId] : undefined;
  const isImage = selectedFile?.type ? selectedFile.type.includes('image') : false;
  const isPdf = selectedFile?.type ? selectedFile.type.includes('pdf') : false;

  return (
    <ProfessionalDialog
      actions={<DialogActionButtons confirmText="Close" onCancel={onClose} onConfirm={onClose} />}
      contentSx={{ pt: 2, px: 3, pb: 2 }}
      icon={<PaperclipIcon size={20} />}
      maxWidth="md"
      onClose={onClose}
      open={open}
      subtitle={`Total files: ${files?.length ?? 0}`}
      title="Announcement Attachments"
    >
      {(!files || files.length === 0) ? (
        <Typography color="text.secondary" variant="body2">No attachments for this announcement.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          {/* File list */}
          <Stack spacing={1} sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
            {files.map((file, idx) => {
              const id = String(file.id ?? file.key);
              const selected = id === selectedId;
              return (
                <Box
                  key={`${file.key}-${idx}`}
                  onClick={() => { setSelectedId(id); }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: selected ? 'action.selected' : 'action.hover',
                    border: '1px solid',
                    borderColor: selected ? 'primary.main' : 'divider',
                    cursor: 'pointer',
                  }}
                >
                  <SvgIcon fontSize="small">
                    <PaperclipIcon />
                  </SvgIcon>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 600 }} title={file.name} variant="body2">
                      {file.name || 'Attachment'}
                    </Typography>
                    <Typography color="text.secondary" variant="caption">
                      {(file.size / 1024 / 1024).toFixed(1)} MB
                    </Typography>
                    {file.type ? <Chip label={file.type} size="small" sx={{ ml: 1 }} variant="outlined" /> : null}
                  </Box>
                </Box>
              );
            })}
          </Stack>

          {/* Preview area */}
          <Box sx={{ flex: 1, minHeight: '50vh', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, overflow: 'hidden' }}>
            {!selectedFile || !selectedId ? (
              <Typography color="text.secondary" variant="body2">Select a file to preview</Typography>
            ) : !selectedUrl ? (
              <Box alignItems="center" display="flex" height="100%" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : isPdf ? (
              <iframe src={selectedUrl} style={{ width: '100%', height: '70vh', border: 'none' }} title={selectedFile.name} />
            ) : isImage ? (
              <img alt={selectedFile.name} src={selectedUrl} style={{ maxWidth: '100%', height: 'auto' }} />
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography sx={{ mb: 1 }} variant="subtitle2">{selectedFile.name}</Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
                  Preview not available for this file type. You can download the file.
                </Typography>
                <Button href={selectedUrl} startIcon={<DownloadIcon size={16} />} target="_blank" variant="contained">Download</Button>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </ProfessionalDialog>
  );
}


